import { forwardRef } from 'react'
import classNames from 'classnames'

type Props = Omit<React.HTMLProps<HTMLInputElement>, 'inputMode' | 'size' | 'value'> & {
  disabled?: boolean
  label?: string
  description?: string
}

/**
 * @group Components
 * @category Component
 */
export const Toggle = forwardRef(
  ({ className, description, label, disabled, ...restProps }: Props, ref: React.Ref<HTMLInputElement>) => {
    return (
      <div className="flex h-6 flex-row items-center">
        <label className={classNames('relative inline-flex cursor-pointer items-center', className)}>
          <input type="checkbox" className="peer sr-only" ref={ref} disabled={disabled} {...restProps} />
          <div
            className={classNames(
              'rounded-full bg-neutral-100',
              "after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow-sm after:transition-all after:content-['']",
              'peer-checked:after:translate-x-full peer-focus:outline-none',
              'h-6 w-11 peer-focus-within:h-7 peer-focus-within:w-12 peer-focus-within:border-2 peer-focus-within:after:left-1',
              disabled
                ? 'bg-neutral-300 after:bg-neutral-100 peer-checked:bg-neutral-300'
                : 'active:h-7 active:w-12 peer-checked:bg-primary-500 peer-checked:hover:border-primary-600 peer-checked:hover:bg-primary-600'
            )}
          />
        </label>
        {label && (
          <div className={classNames('ml-2 flex flex-col text-sm', description ? 'mt-1' : 'mt-[2px]')}>
            {label && <span className={classNames('text-gray-900', description && 'font-semibold')}>{label}</span>}
            {description && <span className="text-neutral-500">{description}</span>}
          </div>
        )}
      </div>
    )
  }
)

Toggle.displayName = 'Toggle'
